import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Blockchain-redux becomes useful, part 1",
  "description": "",
  "published": "2018-04-03T00:00:00.000Z",
  "redirect_from": ["/blog/blockchain-redux-becomes-useful-part-1/swizec/8311"],
  "categories": "blockchain, Front End, Livecoding, redux, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "D3WH5nI9Nj4",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Remember my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/blockchain-redux-experiment-part-2/swizec/8055"
      }}>{`blockchain-redux`}</a>{` project from December? Probably not.`}</p>
    <p>{`I'm building a library that lets anyone who can build a webapp build a blockchain. No, not an Etherium-distributed app or a crypto currency. Altho you could…`}</p>
    <p>{`No, I'm exploring the idea that you can use blockchains as generic validated data storage. Got some state you wanna save and share? `}<inlineCode parentName="p">{`.dispatch`}</inlineCode>{` an action. Your state is hashed, shared, and saved to `}<em parentName="p">{`a`}</em>{` blockchain.`}</p>
    <p>{`Every other app or client attached to the same blockchain can read your state, use it, and add to it.`}</p>
    <p><img parentName="p" {...{
        "src": "/482c119ee84c855a7c5a53bb80edf0e6/media-krhB8ydCQiYZq-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Ideally, you shouldn't have to know `}<em parentName="p">{`how`}</em>{` it works.`}</p>
    <p>{`My ultimate goal is to give you this 👇`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`import { createStore } from 'blockchain-redux'`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`blockchain = createStore(rootReducer)`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`blockchain.dispatch()`}</inlineCode>{` and `}<inlineCode parentName="li">{`blockchain.getState()`}</inlineCode></li>
      <li parentName="ol">{`You have a globally distributed validated data store`}</li>
    </ol>
    <p>{`This weekend, we got very close.`}</p>
    <h2 {...{
      "id": "first-usable-proof-of-concept"
    }}>{`First usable proof of concept`}</h2>
    <p>{`To prove that blockchains are useful for more than crypto speculation, I'm making my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/building-interactive-dom-benchmark-preliminary-results/swizec/8219"
      }}>{`interactive DOM benchmark`}</a>{` collaborative. You can try it here: `}<a parentName="p" {...{
        "href": "http://dom-benchmark.now.sh/"
      }}>{`dom-benchmark.now.sh`}</a></p>
    <p>{`There's a few kinks left to resolve before I can publish the collaborative version.`}</p>
    <p>{`The goal is to store and share measurements from anyone who plays with the benchmark and show them as live-updating graphs and charts. We can finally find out how fast various web frameworks are in the real world on real people's computers. Won't that be fun? 🤘`}</p>
    <p>{`Right now, on `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=D3WH5nI9Nj4&feature=youtu.be&t=3345"
      }}>{`my local machine`}</a>{`, each click adds a block to the blockchain, and every other instance has to reload the page to fetch the updates. I want to make this work live.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/Screen-Shot-on-2018-04-03-at-124014.png",
        "alt": null
      }}></img></p>
    <p>{`Each block holds data that looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
    "vanilla": {
        "prepend1000": [35, 92, 31, 33, 33, 7],
        "insert1000": [27],
        "append1000": [19, 7],
        "dropAll": [23, 21],
        "remove1": [0]
    },
    "smart": {
        // ...
    },
    "react": {
        // ...
    },
    "preact": {
        // ...
    },
    "vue": {
        // ...
    }
}
`}</code></pre>
    <p>{`A mapping of benchmark types to results. An array of values for each button you can click.`}</p>
    <h3 {...{
      "id": "adding-blockchain-redux-to-the-benchmark"
    }}>{`Adding blockchain-redux to the benchmark`}</h3>
    <p>{`Saving benchmark data to the blockchain revealed a few bugs in `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/blockchain-redux"
      }}>{`blockchain-redux`}</a>{` that I fixed. Unfortunately video of that livecoding session is blocked due to background music. Oops.`}</p>
    <p>{`When the bugs were resolved, integration boiled down to this 👇`}</p>
    <ol>
      <li parentName="ol">{`In `}<inlineCode parentName="li">{`App.js`}</inlineCode>{`, we initialize the blockchain store`}</li>
      <li parentName="ol">{`We subscribe to changes on the blockchain`}</li>
      <li parentName="ol">{`We use the React 16.3 context API to share the store with child components`}</li>
      <li parentName="ol">{`We render the blockchain length counter in `}<inlineCode parentName="li">{`<IntroCopy />`}</inlineCode></li>
      <li parentName="ol">{`We dispatch block additions on every benchmark interaction`}</li>
    </ol>
    <h3 {...{
      "id": "initialize-and-subscribe"
    }}>{`Initialize and subscribe`}</h3>
    <p>{`First 2 steps aren't too hard:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// App.js
import { createStore, firebaseMiddleware } from "blockchain-redux";
import * as firebase from "firebase";

class App extends Component {
    blockchain = {};

    componentDidMount = async () => {
        const FirebaseApp = firebase.initializeApp({
            apiKey: "<api key="">",
            databaseURL: "https://blockchain-dom-benchmark.firebaseio.com",
            projectId: "blockchain-dom-benchmark"
        });

        this.blockchain = await createStore(
            benchmarkReducer,
            firebaseMiddleware(FirebaseApp)
        );

        this.unsubscribe = this.blockchain.subscribe(() => {
            this.forceUpdate();
        });
        this.forceUpdate();
    };

    componentWillUnmount() {
        this.unsubscribe();
    }
</api>
`}</code></pre>
    <p>{`We initialize `}<inlineCode parentName="p">{`blockchain`}</inlineCode>{` as an empty object, then use `}<inlineCode parentName="p">{`createStore`}</inlineCode>{` to overwrite it with our blockchain store in `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`. This is because it needs to talk to Firebase during initialization.`}</p>
    <p>{`Firebase is used as a central hub right now. Eventually, I want to replace it with direct communication between nodes via WebRTC or similar.`}</p>
    <p>{`Once the blockchain is initialized, we subscribe to changes and trigger a `}<inlineCode parentName="p">{`forceUpdate`}</inlineCode>{`. The initial call to subscribers after our chain initializes happens before we have a chance to subscribe.`}</p>
    <p>{`Right before `}<inlineCode parentName="p">{`<App />`}</inlineCode>{` unmounts, we unsubscribe from the store to avoid dangling callbacks.`}</p>
    <h3 {...{
      "id": "use-context-to-pass-the-chain-around"
    }}>{`Use context to pass the chain around`}</h3>
    <p>{`We use the new React 16.3 context API to pass the chain to child components.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// BlockchainContext.js
import React from "react";

const BlockchainContext = React.createContext({});

export default BlockchainContext;
`}</code></pre>
    <p>{`We create a new context in a separate file. This allows us to import it in other components so we can define consumers.`}</p>
    <p>{`In App.js, we set its value like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// App.js
render() {
    return <blockchaincontext class="provider" value={this.blockchain}>
            <introcopy>

            <benchmarks>
        </benchmarks></introcopy></blockchaincontext>
}
`}</code></pre>
    <p>{`We now have `}<inlineCode parentName="p">{`this.blockchain`}</inlineCode>{` shared via context. Any component that needs to read from our blockchain has to use `}<inlineCode parentName="p">{`<BlockchainContext.Consumer>`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "read-from-the-chain"
    }}>{`Read from the chain`}</h3>
    <p>{`In `}<inlineCode parentName="p">{`<IntroCopy>`}</inlineCode>{`, we render the current length of our blockchain like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import BlockchainContext from "./BlockchainContext";

export default () => (
    <react class="fragment">
        <div class="App-heading App-flex">
            <h2>
                Let's benchmark the <span class="App-react">DOM</span>{" "}
                together
            </h2>
        </div>
        <div class="App-instructions App-flex">
            <blockchaincontext class="consumer">
                {({ getWholeChain = () => [] }) => (
                    There are {getWholeChain().length} nodes on the chain
                )}
            </blockchaincontext>
</div></react>
`}</code></pre>
    <p>{`We wrap a paragraph in a `}<inlineCode parentName="p">{`BlockchainContext.Consumer`}</inlineCode>{`, which takes a function as children. That function gets the context `}<inlineCode parentName="p">{`value`}</inlineCode>{` as its argument, which lets us call `}<inlineCode parentName="p">{`getWholeChain()`}</inlineCode>{` on our blockchain to read its length.`}</p>
    <p>{`Another approach would be the traditional connected component pattern. We'd make a new `}<inlineCode parentName="p">{`BlockchainLength`}</inlineCode>{` component, subscribe it to blockchain changes, and render values.`}</p>
    <p>{`Just like Redux 🤓`}</p>
    <h3 {...{
      "id": "adding-to-the-chain"
    }}>{`Adding to the chain`}</h3>
    <p>{`Adding new benchmark values to the chain works via action dispatchers. Just like Redux.`}</p>
    <p><inlineCode parentName="p">{`<VanillaBenchmark>`}</inlineCode>{` (and soon all the others) takes an `}<inlineCode parentName="p">{`addToBenchmark`}</inlineCode>{` function as props and calls it after updating its own benchmark display.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// benchmarks/VanillaBenchmark.js

updateValues() {
    // render stuff

    this.props.addToBenchmark({
        name: this.props.name,
        value: end - start,
        type
    });
}
`}</code></pre>
    <p>{`Name is the name of this benchmark (`}<inlineCode parentName="p">{`vanilla`}</inlineCode>{`), value is the result, and type tells us which button you clicked.`}</p>
    <p>{`The overall `}<inlineCode parentName="p">{`<Benchmarks />`}</inlineCode>{` component takes this callback and dispatches an action on the blockchain.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`addToBenchmark = ({ name, type, value }) => {
  this.props.blockchain.dispatch({
    actionType: "ADD_BENCHMARK",
    name,
    type,
    value,
  });
};
`}</code></pre>
    <p>{`Looks familiar, doesn't it? It's just like Redux. You dispatch an action and reducers handle calculating the new state.`}</p>
    <p>{`Our reducer for this example doesn't do much more than concat values to arrays. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const benchmarkReducer = function (name) {
  return function (state = defaultState, action) {
    if (name !== action.name) {
      return state;
    }

    switch (action.actionType) {
      case "ADD_BENCHMARK":
        return Object.assign(state, {
          [action.type]: [...state[action.type], action.value],
        });
      default:
        return state;
    }
  };
};

export default combineReducers({
  vanilla: benchmarkReducer("vanilla"),
  smart: benchmarkReducer("smart"),
  react: benchmarkReducer("react"),
  preact: benchmarkReducer("preact"),
  vue: benchmarkReducer("vue"),
});
`}</code></pre>
    <p>{`All our reducers are the same, but they use different names. `}<inlineCode parentName="p">{`benchmarkReducer`}</inlineCode>{` is a function that takes a name and returns a generic benchmark reducer. It appends values to the specified type's list of measurements.`}</p>
    <p>{`Just like Redux 🤓`}</p>
    <h3 {...{
      "id": "just-like-redux-but-more-"
    }}>{`Just like Redux, but more 🤓`}</h3>
    <p>{`Unlike just Redux, magic happens, and a new block is added to the blockchain.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/screenshot-gif.gif",
        "alt": null
      }}></img></p>
    <p>{`And you thought it wasn't useful. Ha!`}</p>
    <h2 {...{
      "id": "what-next"
    }}>{`What next?`}</h2>
    <p>{`Next step for this particular example is to render some graphs and launch the collaborative benchmark. We'll see what happens.`}</p>
    <p>{`On the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/blockchain-redux"
      }}>{`blockchain-redux`}</a>{` front, I'm most probably giving a lightning talk about it at `}<a parentName="p" {...{
        "href": "https://www.frontiersf.com/"
      }}>{`Frontier Conf`}</a>{` in April and a more proper talk at `}<a parentName="p" {...{
        "href": "wearedevelopers.com/congress/"
      }}>{`WeAreDevelopers`}</a>{` in May.`}</p>
    <p>{`Fun times. No pressure. 😅`}</p>
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      